import React from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { MessageCircle, Phone, Mail, Instagram, Facebook, MapPin, ArrowUpRight, Scissors } from "lucide-react";
import { NAV, CONTACT } from "./siteData";

export const SWATCHES = [
  "repeating-linear-gradient(135deg,#b8402f 0 6px,#1f3a5f 6px 12px,#d4a24c 12px 18px)",
  "radial-gradient(#e9d8a6 2px,transparent 2.6px) 0 0/10px 10px,#1f2f5a",
  "repeating-linear-gradient(90deg,#7a3b69 0 5px,#a65a8f 5px 10px)",
  "radial-gradient(#f3e3c3 2.4px,transparent 3px) 0 0/11px 11px,#6b3f22",
  "repeating-linear-gradient(0deg,#1f5136 0 5px,#e8efe6 5px 8px)",
  "conic-gradient(from 45deg,#b5432f 25%,#f1e3d3 0 50%,#b5432f 0 75%,#f1e3d3 0) 0 0/12px 12px",
  "repeating-linear-gradient(45deg,#151515 0 5px,#e9dcc5 5px 10px)",
  "radial-gradient(#7a1f2b 2.4px,transparent 3px) 0 0/10px 10px,#efe2c6",
];

const zigBottom = (n, d) => {
  const p = ["0 0", "100% 0", `100% calc(100% - ${d}px)`];
  for (let i = n - 1; i >= 0; i--) p.push(`${((i + 0.5) / n) * 100}% 100%`, `${(i / n) * 100}% calc(100% - ${d}px)`);
  return `polygon(${p.join(",")})`;
};
const zigSides = (n = 5, d = 6) => {
  const p = ["0 0", "100% 0"];
  for (let i = 0; i < n; i++) p.push(`calc(100% - ${d}px) ${((i + 0.5) / n) * 100}%`, `100% ${((i + 1) / n) * 100}%`);
  p.push("0 100%");
  for (let i = n - 1; i >= 0; i--) p.push(`${d}px ${((i + 0.5) / n) * 100}%`, `0 ${(i / n) * 100}%`);
  return `polygon(${p.join(",")})`;
};
const TAG = zigSides(5, 6);
const EASE = [0.22, 1, 0.36, 1];
const up = (d = 0) => ({
  initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" }, transition: { delay: d, duration: 0.7, ease: EASE },
});

function FabricTag({ as = "a", primary, children, ...rest }) {
  const C = as === "link" ? Link : "a";
  return (
    <motion.div whileHover={{ rotate: -2, y: -3 }} whileTap={{ scale: 0.97 }} transition={{ type: "spring", stiffness: 300, damping: 15 }}>
      <C {...rest} style={{ clipPath: TAG }}
        className={`relative inline-flex items-center gap-2 px-8 py-3.5 text-sm tracking-wide transition-colors duration-500 ${
          primary ? "bg-[#B89B72] pl-11 font-medium text-[#1A1A1A] hover:bg-[#FAF9F6]" : "bg-white/[0.07] text-[#FAF9F6] backdrop-blur-sm hover:bg-white/[0.14]"}`}>
        <span className={`pointer-events-none absolute inset-[5px] border border-dashed ${primary ? "border-[#1A1A1A]/25" : "border-white/20"}`} />
        {primary && <span className="absolute left-4 top-1/2 h-2 w-2 -translate-y-1/2 rounded-full bg-[#1A1A1A] ring-2 ring-[#8B6B3A]/40" />}
        {children}
      </C>
    </motion.div>
  );
}

export function StudioCTA() {
  const reduce = useReducedMotion();
  const words = "Let's create something exceptional together.".split(" ");
  return (
    <section className="relative overflow-hidden bg-[#1A1A1A] pb-20 pt-0 text-center lg:pb-24" data-testid="home-cta">
      <motion.div aria-hidden className="absolute -inset-x-24 inset-y-0 opacity-70"
        style={{ background: "repeating-linear-gradient(90deg,rgba(255,255,255,0.035) 0,rgba(0,0,0,0.35) 70px,rgba(255,255,255,0.035) 120px)" }}
        animate={reduce ? {} : { x: [0, -48, 0], skewX: [0, 1.5, 0] }} transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }} />
      <div aria-hidden className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 45%,transparent 25%,#1A1A1A 78%)" }} />
      <div className="relative mx-auto max-w-4xl px-6">
        <div className="h-[3px] rounded-b-full bg-gradient-to-r from-[#4a3220] via-[#8B6B3A] to-[#4a3220]" />
        <div className="flex justify-between px-2 sm:px-10">
          {SWATCHES.map((s, i) => (
            <motion.div key={i} className={i > 4 ? "hidden sm:block" : ""}
              initial={{ opacity: 0, y: -24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.07, duration: 0.6, ease: EASE }}>
              <motion.div style={{ transformOrigin: "50% 0%" }}
                animate={reduce ? {} : { rotate: [-2.5, 2.5, -2.5] }} transition={{ duration: 3.4 + i * 0.35, repeat: Infinity, ease: "easeInOut" }}>
                <div className="mx-auto h-4 w-px bg-[#8B6B3A]" />
                <div className="h-16 w-10 shadow-[0_10px_20px_rgba(0,0,0,0.45)] sm:h-20 sm:w-12" style={{ background: s, clipPath: zigBottom(4, 6) }} />
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
      <div className="relative mx-auto mt-12 max-w-4xl px-6">
        <div className="relative rounded-[22px] px-6 py-14 sm:px-12">
          <svg aria-hidden className="pointer-events-none absolute inset-0 h-full w-full">
            <motion.rect x="1" y="1" width="99.6%" height="99.2%" rx="20" fill="none" stroke="#B89B72" strokeOpacity="0.45"
              strokeWidth="1.5" strokeDasharray="9 8"
              animate={reduce ? {} : { strokeDashoffset: [0, -51] }} transition={{ duration: 3, repeat: Infinity, ease: "linear" }} />
          </svg>
          <motion.p {...up(0)} className="flex items-center justify-center gap-4 text-[11px] uppercase tracking-[0.32em] text-[#B89B72]">
            <span className="h-px w-10 bg-[#B89B72]/70" />Get in touch<span className="h-px w-10 bg-[#B89B72]/70" />
          </motion.p>
          <h2 className="font-display mx-auto mt-6 max-w-3xl text-4xl font-light leading-[1.1] text-[#FAF9F6] sm:text-5xl lg:text-6xl">
            {words.map((w, i) => (
              <motion.span key={i} className="mr-[0.25em] inline-block"
                initial={{ opacity: 0, y: 26, filter: "blur(6px)" }} whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                viewport={{ once: true }} transition={{ delay: 0.15 + i * 0.08, duration: 0.7, ease: EASE }}>{w}</motion.span>
            ))}
          </h2>
          <motion.p {...up(0.5)} className="mx-auto mt-5 max-w-md text-sm leading-relaxed text-white/55">
            Tell us your fabric, size and finish — we'll print it to perfection.
          </motion.p>
          <motion.div {...up(0.65)} className="mt-10 flex flex-wrap justify-center gap-3">
            <FabricTag as="link" to="/contact" primary data-testid="cta-quote-btn">Request Quote <ArrowUpRight className="h-4 w-4" /></FabricTag>
            <FabricTag href={`https://wa.me/${CONTACT.whatsapp}`} data-testid="cta-whatsapp-btn"><MessageCircle className="h-4 w-4" />WhatsApp</FabricTag>
            <FabricTag href={`tel:${CONTACT.phone}`} data-testid="cta-call-btn"><Phone className="h-4 w-4" />Call</FabricTag>
            <FabricTag href={`mailto:${CONTACT.email}`} data-testid="cta-email-btn"><Mail className="h-4 w-4" />Email</FabricTag>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function SewButton({ href, label, testid, children }) {
  return (
    <motion.a href={href} target="_blank" rel="noreferrer" aria-label={label} data-testid={testid}
      whileHover={{ rotate: 90, scale: 1.08 }} transition={{ type: "spring", stiffness: 260, damping: 14 }}
      className="relative grid h-10 w-10 place-items-center rounded-full border border-white/15 bg-white/[0.04] text-white/60 transition-colors hover:border-[#B89B72] hover:text-[#B89B72]">
      <span className="pointer-events-none absolute inset-[4px] rounded-full border border-dashed border-white/10" />
      {children}
    </motion.a>
  );
}

const THREAD_LINK = "relative w-fit text-sm text-white/70 transition-colors hover:text-[#B89B72] after:absolute after:-bottom-1 after:left-0 after:w-0 after:border-b after:border-dashed after:border-[#B89B72] after:transition-all after:duration-500 hover:after:w-full";

export function StudioFooter() {
  const reduce = useReducedMotion();
  const ribbon = [...SWATCHES, ...SWATCHES, ...SWATCHES, ...SWATCHES];
  return (
    <footer className="relative overflow-hidden bg-[#141211] text-[#FAF9F6]" data-testid="site-footer">
      <div className="relative h-4 overflow-hidden" style={{ clipPath: zigBottom(70, 5) }} aria-hidden>
        <motion.div className="flex h-full w-[200%]" animate={reduce ? {} : { x: ["0%", "-50%"] }} transition={{ duration: 45, repeat: Infinity, ease: "linear" }}>
          {ribbon.map((s, i) => <div key={i} className="h-full flex-1" style={{ background: s }} />)}
        </motion.div>
      </div>
      <svg aria-hidden className="pointer-events-none absolute left-0 top-12 h-48 w-full opacity-25" viewBox="0 0 1200 160" preserveAspectRatio="none">
        <motion.path d="M0 120 C 200 20, 400 160, 600 80 S 1000 10, 1200 90" fill="none" stroke="#B89B72" strokeWidth="1.2"
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 2.6, ease: "easeInOut" }} />
      </svg>
      <div className="relative mx-auto grid max-w-[1400px] gap-12 px-6 pb-12 pt-20 md:grid-cols-4 lg:px-12">
        <motion.div {...up(0)} className="md:col-span-2">
          <p className="font-display text-3xl tracking-[0.14em]">NAYARA<span className="text-[#B89B72]"> STUDIO</span></p>
          <div className="mt-4 flex gap-1.5" aria-hidden>
            {SWATCHES.map((s, i) => (
              <motion.span key={i} className="h-4 w-4 rounded-full ring-1 ring-white/10" style={{ background: s }}
                initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ delay: 0.3 + i * 0.05, type: "spring", stiffness: 300 }} />
            ))}
          </div>
          <p className="mt-5 max-w-sm text-sm leading-relaxed text-white/55">{CONTACT.tagline}. Crafting premium backdrops and printed textiles for photographers, events and interiors.</p>
          <Link to="/contact" className={`${THREAD_LINK} mt-6 inline-flex items-center gap-1.5 !text-[#B89B72]`}>Request a quote <ArrowUpRight className="h-3.5 w-3.5" /></Link>
        </motion.div>
        <motion.div {...up(0.1)}>
          <p className="flex items-center gap-2 text-[0.68rem] uppercase tracking-[0.28em] text-white/40"><Scissors className="h-3.5 w-3.5 text-[#B89B72]" />Explore</p>
          <div className="mt-5 flex flex-col gap-3">
            {NAV.map((n) => <Link key={n.to} to={n.to} className={THREAD_LINK}>{n.label}</Link>)}
          </div>
        </motion.div>
        <motion.div {...up(0.2)}>
          <p className="text-[0.68rem] uppercase tracking-[0.28em] text-white/40">Reach us</p>
          <div className="mt-5 flex flex-col gap-4 text-sm text-white/70">
            <a href={`tel:${CONTACT.phone}`} className={`${THREAD_LINK} flex items-center gap-3`}><Phone className="h-4 w-4 text-[#B89B72]" />{CONTACT.phone}</a>
            <a href={`https://wa.me/${CONTACT.whatsapp}`} className={`${THREAD_LINK} flex items-center gap-3`}><MessageCircle className="h-4 w-4 text-[#B89B72]" />WhatsApp</a>
            <a href={`mailto:${CONTACT.email}`} className={`${THREAD_LINK} flex items-center gap-3`}><Mail className="h-4 w-4 text-[#B89B72]" />{CONTACT.email}</a>
            <span className="flex items-start gap-3"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#B89B72]" />{CONTACT.address}</span>
          </div>
        </motion.div>
      </div>
      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-12" aria-hidden>
        <svg className="h-2 w-full">
          <motion.line x1="0" y1="4" x2="100%" y2="4" stroke="rgba(184,155,114,0.45)" strokeWidth="1.5" strokeDasharray="10 7"
            animate={reduce ? {} : { strokeDashoffset: [0, -34] }} transition={{ duration: 2.5, repeat: Infinity, ease: "linear" }} />
        </svg>
      </div>
      <div className="relative mx-auto flex max-w-[1400px] flex-col items-center justify-between gap-5 px-6 py-8 text-xs text-white/40 md:flex-row lg:px-12">
        <span>© {new Date().getFullYear()} Nayara Studio. All rights reserved.</span>
        <div className="flex items-center gap-3">
          <SewButton href={CONTACT.instagram} label="Instagram" testid="footer-instagram"><Instagram className="h-4 w-4" /></SewButton>
          <SewButton href={CONTACT.facebook} label="Facebook" testid="footer-facebook"><Facebook className="h-4 w-4" /></SewButton>
          <SewButton href={`https://wa.me/${CONTACT.whatsapp}`} label="WhatsApp" testid="footer-whatsapp"><MessageCircle className="h-4 w-4" /></SewButton>
        </div>
        <Link to="/login" className={`${THREAD_LINK} !text-xs !text-white/40`} data-testid="footer-erp-login">Client / Team Login</Link>
      </div>
    </footer>
  );
}
